/**
 * Mermaid kanban parser
 * Extracts columns and cards from Mermaid kanban syntax
 */

export interface KanbanCardMetadata {
  assigned?: string;
  priority?: string;
  ticket?: string;
  [key: string]: string | undefined;
}

export interface KanbanCard {
  id: string;
  label: string;
  metadata: KanbanCardMetadata;
}

export interface KanbanColumn {
  id: string;
  label: string;
  cards: KanbanCard[];
}

export interface ParsedKanban {
  columns: KanbanColumn[];
}

interface ParsedCardPointer {
  columnIndex: number;
  cardIndex: number;
}

interface ParsedKanbanItem {
  id: string;
  label: string;
  indentWidth: number;
}

interface KanbanItemBuildInput {
  line: string;
  itemId: string | undefined;
  rawLabel: string | undefined;
}

interface KanbanParseState {
  columnIndent: number;
  columns: KanbanColumn[];
  currentColumnIndex: number | null;
  lastParsedCardPointer: ParsedCardPointer | null;
}

interface KanbanIgnoredLineInstruction {
  kind: KanbanLineInstructionKind.Ignored;
}

interface KanbanItemLineInstruction {
  kind: KanbanLineInstructionKind.Item;
  item: ParsedKanbanItem;
}

interface KanbanMetadataLineInstruction {
  kind: KanbanLineInstructionKind.Metadata;
  metadata: KanbanCardMetadata;
}

type KanbanLineInstruction =
  | KanbanIgnoredLineInstruction
  | KanbanItemLineInstruction
  | KanbanMetadataLineInstruction;

interface KanbanLineReducerInput {
  state: KanbanParseState;
  instruction: KanbanLineInstruction;
}

interface KanbanEntityReducerInput {
  state: KanbanParseState;
  item: ParsedKanbanItem;
}

interface KanbanMetadataTarget {
  pointer: ParsedCardPointer;
}

enum KanbanLineInstructionKind {
  Ignored = 'ignored',
  Item = 'item',
  Metadata = 'metadata',
}

enum KanbanEntityKind {
  Card = 'card',
  Column = 'column',
}

const KANBAN_HEADER_REGEX = /^kanban\b/i;
const COLUMN_OR_CARD_REGEX = /^(\w[\w-]*)\[([^\]]+)\]$/;
const METADATA_REGEX = /^@\{\s*(.+)\s*\}$/;
const METADATA_ENTRY_REGEX = /(\w+)\s*:\s*"([^"]*)"/g;
// Mermaid accepts a bare label wherever `id[Label]` is valid, and a bare label
// is what models write and what the seeded guide teaches. A bare line is
// rejected when it carries a square bracket — the well-formed `id[Label]` shape
// already matched, so a leftover bracket is a malformed one — or a mermaid edge
// token, which means the body is another diagram's syntax rather than a card.
const BARE_LABEL_BRACKET_REGEX = /[[\]]/;
const BARE_LABEL_EDGE_TOKEN_REGEX = /--+[>ox]|-\.-+>|==+>|->>/;
// Unicode-aware so a Hebrew label still derives a non-empty id.
const LABEL_ID_UNSAFE_CHARS_REGEX = /[^\p{L}\p{N}]+/gu;
const LABEL_ID_EDGE_DASHES_REGEX = /^-+|-+$/g;

function sanitizeMermaidLine(line: string): string {
  const commentIndex = line.indexOf('%%');
  const withoutComment = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
  return withoutComment.replace(/;\s*$/, '').replace(/\s+$/, '');
}

function getIndentWidth(line: string): number {
  const leadingWhitespaceMatch = line.match(/^[\t ]*/);
  const leadingWhitespace = leadingWhitespaceMatch?.[0] ?? '';

  return [...leadingWhitespace].reduce((indent, char) => {
    return indent + (char === '\t' ? 2 : 1);
  }, 0);
}

function parseMetadata(rawMetadata: string): KanbanCardMetadata {
  return Array.from(rawMetadata.matchAll(METADATA_ENTRY_REGEX)).reduce<KanbanCardMetadata>((metadata, match) => {
    const key = match[1];
    const value = match[2];

    if (!key) {
      return metadata;
    }

    return {
      ...metadata,
      [key]: value ?? '',
    };
  }, {});
}

function hasKanbanLineContent(line: string): boolean {
  return line.trim().length > 0;
}

function isKanbanHeaderLine(line: string): boolean {
  return KANBAN_HEADER_REGEX.test(line.trim());
}

function isBareKanbanLabel(trimmedLine: string): boolean {
  if (trimmedLine.length === 0) {
    return false;
  }

  return (
    !BARE_LABEL_BRACKET_REGEX.test(trimmedLine) &&
    !BARE_LABEL_EDGE_TOKEN_REGEX.test(trimmedLine) &&
    !METADATA_REGEX.test(trimmedLine)
  );
}

function isKanbanEntityLine(line: string): boolean {
  const trimmedLine = line.trim();
  return COLUMN_OR_CARD_REGEX.test(trimmedLine) || isBareKanbanLabel(trimmedLine);
}

/**
 * A bare line has no author-supplied id, so the label is the id. Ids are only
 * ever React keys and hover-map keys here, so the raw label is a valid last
 * resort when a label carries no letters or digits at all.
 */
function toKanbanIdFromLabel(label: string): string {
  const slug = label
    .toLowerCase()
    .replace(LABEL_ID_UNSAFE_CHARS_REGEX, '-')
    .replace(LABEL_ID_EDGE_DASHES_REGEX, '');

  return slug.length > 0 ? slug : label;
}

function buildKanbanItem(input: KanbanItemBuildInput): ParsedKanbanItem | null {
  if (!input.itemId) {
    return null;
  }

  const itemLabel = (input.rawLabel ?? '').trim();
  if (itemLabel.length === 0) {
    return null;
  }

  return {
    id: input.itemId,
    label: itemLabel,
    indentWidth: getIndentWidth(input.line),
  };
}

function parseKanbanItem(input: { line: string; trimmedLine: string }): ParsedKanbanItem | null {
  const itemMatch = input.trimmedLine.match(COLUMN_OR_CARD_REGEX);
  if (itemMatch) {
    return buildKanbanItem({
      line: input.line,
      itemId: itemMatch[1],
      rawLabel: itemMatch[2],
    });
  }

  if (!isBareKanbanLabel(input.trimmedLine)) {
    return null;
  }

  return buildKanbanItem({
    line: input.line,
    itemId: toKanbanIdFromLabel(input.trimmedLine),
    rawLabel: input.trimmedLine,
  });
}

function selectKanbanLineInstruction(line: string): KanbanLineInstruction {
  const trimmedLine = line.trim();
  const metadataMatch = trimmedLine.match(METADATA_REGEX);
  if (metadataMatch) {
    return {
      kind: KanbanLineInstructionKind.Metadata,
      metadata: parseMetadata(metadataMatch[1] ?? ''),
    };
  }

  const item = parseKanbanItem({
    line,
    trimmedLine,
  });
  if (!item) {
    return {
      kind: KanbanLineInstructionKind.Ignored,
    };
  }

  return {
    kind: KanbanLineInstructionKind.Item,
    item,
  };
}

function selectKanbanEntityKind(input: { item: ParsedKanbanItem; columnIndent: number }): KanbanEntityKind {
  return input.item.indentWidth <= input.columnIndent ? KanbanEntityKind.Column : KanbanEntityKind.Card;
}

function selectMetadataTarget(state: KanbanParseState): KanbanMetadataTarget | null {
  const pointer = state.lastParsedCardPointer;
  if (!pointer) {
    return null;
  }

  const targetColumn = state.columns[pointer.columnIndex];
  const targetCard = targetColumn?.cards[pointer.cardIndex];
  if (!targetCard) {
    return null;
  }

  return {
    pointer,
  };
}

function applyIgnoredKanbanLine(input: KanbanLineReducerInput): KanbanParseState {
  return input.state;
}

function applyMetadataKanbanLine(input: KanbanLineReducerInput): KanbanParseState {
  if (input.instruction.kind !== KanbanLineInstructionKind.Metadata) {
    return input.state;
  }

  const target = selectMetadataTarget(input.state);
  if (!target) {
    return input.state;
  }

  const metadata = input.instruction.metadata;

  return {
    ...input.state,
    columns: input.state.columns.map((column, columnIndex) => {
      if (columnIndex !== target.pointer.columnIndex) {
        return column;
      }

      return {
        ...column,
        cards: column.cards.map((card, cardIndex) => {
          if (cardIndex !== target.pointer.cardIndex) {
            return card;
          }

          return {
            ...card,
            metadata: {
              ...card.metadata,
              ...metadata,
            },
          };
        }),
      };
    }),
  };
}

function appendKanbanColumn(input: KanbanEntityReducerInput): KanbanParseState {
  const nextColumn: KanbanColumn = {
    id: input.item.id,
    label: input.item.label,
    cards: [],
  };

  return {
    ...input.state,
    columns: [...input.state.columns, nextColumn],
    currentColumnIndex: input.state.columns.length,
    lastParsedCardPointer: null,
  };
}

function appendKanbanCard(input: KanbanEntityReducerInput): KanbanParseState {
  if (input.state.currentColumnIndex == null) {
    return input.state;
  }

  const targetColumn = input.state.columns[input.state.currentColumnIndex];
  if (!targetColumn) {
    return input.state;
  }

  const nextCard: KanbanCard = {
    id: input.item.id,
    label: input.item.label,
    metadata: {},
  };

  return {
    ...input.state,
    columns: input.state.columns.map((column, columnIndex) => {
      if (columnIndex !== input.state.currentColumnIndex) {
        return column;
      }

      return {
        ...column,
        cards: [...column.cards, nextCard],
      };
    }),
    lastParsedCardPointer: {
      columnIndex: input.state.currentColumnIndex,
      cardIndex: targetColumn.cards.length,
    },
  };
}

const KANBAN_ENTITY_REDUCERS: Readonly<
  Record<KanbanEntityKind, (input: KanbanEntityReducerInput) => KanbanParseState>
> = {
  [KanbanEntityKind.Card]: appendKanbanCard,
  [KanbanEntityKind.Column]: appendKanbanColumn,
};

function applyItemKanbanLine(input: KanbanLineReducerInput): KanbanParseState {
  if (input.instruction.kind !== KanbanLineInstructionKind.Item) {
    return input.state;
  }

  const entityKind = selectKanbanEntityKind({
    item: input.instruction.item,
    columnIndent: input.state.columnIndent,
  });

  return KANBAN_ENTITY_REDUCERS[entityKind]({
    state: input.state,
    item: input.instruction.item,
  });
}

const KANBAN_LINE_REDUCERS: Readonly<
  Record<KanbanLineInstructionKind, (input: KanbanLineReducerInput) => KanbanParseState>
> = {
  [KanbanLineInstructionKind.Ignored]: applyIgnoredKanbanLine,
  [KanbanLineInstructionKind.Item]: applyItemKanbanLine,
  [KanbanLineInstructionKind.Metadata]: applyMetadataKanbanLine,
};

function applyKanbanBodyLine(input: { state: KanbanParseState; line: string }): KanbanParseState {
  const instruction = selectKanbanLineInstruction(input.line);
  return KANBAN_LINE_REDUCERS[instruction.kind]({
    state: input.state,
    instruction,
  });
}

function parseKanbanBody(input: { bodyLines: string[]; columnIndent: number }): KanbanParseState {
  return input.bodyLines.reduce<KanbanParseState>(
    (state, line) =>
      applyKanbanBodyLine({
        state,
        line,
      }),
    {
      columnIndent: input.columnIndent,
      columns: [],
      currentColumnIndex: null,
      lastParsedCardPointer: null,
    },
  );
}

export function parseMermaidKanban(raw: string): ParsedKanban | null {
  const lines = raw
    .split('\n')
    .map(sanitizeMermaidLine)
    .filter(hasKanbanLineContent);

  const headerIndex = lines.findIndex(isKanbanHeaderLine);
  if (headerIndex === -1) {
    return null;
  }

  const bodyLines = lines.slice(headerIndex + 1);
  const firstKanbanEntityLine = bodyLines.find(isKanbanEntityLine);
  if (!firstKanbanEntityLine) {
    return null;
  }

  const columnIndent = getIndentWidth(firstKanbanEntityLine);
  const parsedState = parseKanbanBody({
    bodyLines,
    columnIndent,
  });

  if (parsedState.columns.length === 0) {
    return null;
  }

  return {
    columns: parsedState.columns,
  };
}
